import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Input,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import AppSelect from "../../components/common/appSelect";

interface EventFiltersProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  locationFilter: string;
  setLocationFilter: (val: string) => void;
  locations: string[];
  handleResetFilters: () => void;
  onCreateEvent: () => void;
}

const EventFilters: React.FC<EventFiltersProps> = ({
  searchQuery,
  setSearchQuery,
  locationFilter,
  setLocationFilter,
  locations,
  handleResetFilters,
  onCreateEvent,
}) => {
  const { t } = useTranslation("events");

  return (
    <>
      <div className="lg:hidden flex items-center gap-2 w-full">
        <div className="flex-1 min-w-0">
          <AppSelect
            placeholder={t("location", "Location")}
            selectedKeys={locationFilter ? [locationFilter] : []}
            onSelectionChange={(keys) => {
              const selected = Array.from(keys)[0] as string;
              setLocationFilter(selected || "");
            }}
            size="sm"
            radius="lg"
            classNames={{
              trigger:
                "bg-white border border-slate-200 shadow-none h-9 px-3 rounded-xl text-sm",
              value: "text-sm text-slate-700 truncate",
            }}
          >
            {locations.map((loc: string) => (
              <SelectItem key={loc} textValue={loc} className="text-sm">
                {loc}
              </SelectItem>
            ))}
          </AppSelect>
        </div>

        <Input
          placeholder={t("search_by_event_name", "Search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startContent={
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 shrink-0" />
          }
          size="sm"
          radius="lg"
          classNames={{
            base: "w-36 shrink-0",
            inputWrapper:
              "bg-white border border-slate-200 shadow-none h-9 px-3 rounded-xl",
            input: "text-sm text-slate-800 placeholder:text-slate-400",
          }}
        />

        <Button
          color="primary"
          isIconOnly
          size="sm"
          className="bg-blue-600 text-white rounded-xl h-9 w-9 min-w-9 shrink-0"
          onPress={onCreateEvent}
          aria-label={t("create_event", "Create event")}
        >
          <PlusIcon className="w-4 h-4 stroke-2" />
        </Button>
      </div>

      <div className="hidden lg:flex flex-wrap items-center justify-between gap-4 w-full">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] shrink-0">
          {t("event_service", "Event service")}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-72">
            <Input
              placeholder={t("search_by_event_name", "Search by event name")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 shrink-0 mr-1" />
              }
              radius="lg"
              size="sm"
              classNames={{
                inputWrapper:
                  "bg-white border border-slate-200/90 shadow-none hover:border-slate-300 focus-within:!border-blue-500 h-9 px-3 rounded-xl",
                input: "text-sm text-slate-800 placeholder:text-slate-400",
              }}
            />
          </div>

          <div className="w-44 sm:w-56">
            <AppSelect
              placeholder={t("location", "Location")}
              selectedKeys={locationFilter ? [locationFilter] : []}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0] as string;
                setLocationFilter(selected || "");
              }}
              radius="lg"
              size="sm"
              classNames={{
                trigger:
                  "bg-white border border-slate-200/90 shadow-none hover:border-slate-300 focus-within:!border-blue-500 h-9 px-3 rounded-xl",
                value: "text-sm text-slate-700 truncate",
              }}
            >
              {locations.map((loc: string) => (
                <SelectItem key={loc} textValue={loc} className="text-sm">
                  {loc}
                </SelectItem>
              ))}
            </AppSelect>
          </div>

          <Tooltip content={t("reset_filters", "Reset Filters")}>
            <Button
              isIconOnly
              variant="light"
              onPress={handleResetFilters}
              className="h-9 w-9 min-w-9 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              size="sm"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </Button>
          </Tooltip>

          <Button
            color="primary"
            startContent={<PlusIcon className="w-4 h-4 stroke-2" />}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-2xl h-9 px-4 shadow-sm"
            size="sm"
            onPress={onCreateEvent}
          >
            {t("create_event", "Create event")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default EventFilters;
